import React, { FunctionComponent } from "react";
import Image from "next/image";
import { AssetDetails } from "contentful";

import {
  IIconWithText,
  IIconWithTextFields,
} from "@/types/generated/contentful";
import RichText from "../richtext";
import CallToAction from "../callToAction";

type Props = {
  content: IIconWithText;
};

const IconWithText: FunctionComponent<Props> = ({ content }) => {
  const {
    icon,
    iconSizing,
    imageAlignment,
    verticalAlignment,
    heading,
    headingSize,
    body,
    textAlignment,
    callToAction,
    background,
    foreground,
    borderColor,
  } = content.fields as IIconWithTextFields;

  let headSize = "";
  switch (headingSize) {
    case "lg":
      headSize = "text-4xl";
      break;
    case "xl":
      headSize = "text-6xl";
      break;
    case "sm":
      headSize = "text-xl";
      break;
    case "md":
    default:
      headSize = "text-2xl";
      break;
  }

  let flexDirection = "";
  let reversed = false;
  switch (imageAlignment) {
    case "left":
      flexDirection = "flex-row";
      break;
    case "right":
      flexDirection = "flex-row";
      reversed = true;
      break;
    case "bottom":
      flexDirection = "flex-col";
      reversed = true;
      break;
    case "top":
    default:
      flexDirection = "flex-col";
      break;
  }

  let textAlign = "text-left";
  switch (textAlignment) {
    case "center":
      textAlign = "text-center";
      break;
    case "right":
      textAlign = "text-right";
      break;
    default:
      textAlign = "text-left";
      break;
  }

  let iconSize = "w-full";
  switch (iconSizing) {
    case "small":
      iconSize = "w-5";
      break;
    case "medium":
      iconSize = "w-16";
      break;
    case "large":
      iconSize = "w-32";
      break;
  }

  let verticalAlign = "";
  switch (verticalAlignment) {
    case "middle":
      verticalAlign = "items-center";
      break;
    case "bottom":
      verticalAlign = "items-end";
      break;
    case "top":
    default:
      verticalAlign = "items-start";
      break;
  }

  return (
    <div
      className={`mx-auto h-full max-w-screen-xl rounded-xl ${!!background && "p-8"}`}
      style={{
        backgroundColor: background || "transparent",
        ...(foreground && { color: foreground }),
        ...(borderColor && { border: `1px solid ${borderColor}` }),
      }}
    >
      <div
        className={`flex ${flexDirection} h-full ${verticalAlign} justify-center gap-4`}
      >
        <Image
          className={`mt-1 ${iconSize}`}
          src={`https:${icon.fields.file?.url}`}
          alt={icon.fields.description as string}
          width={(icon.fields.file?.details as AssetDetails).image?.width}
          height={(icon.fields.file?.details as AssetDetails).image?.height}
          style={{ objectFit: "contain" }}
          priority
        />
        <div
          className={`flex flex-col ${reversed ? "order-1" : "order-2"} ${textAlign}`}
        >
          {heading && <p className={`${headSize} mb-2`}>{heading}</p>}
          {body && (
            <div className="flex">{body && <RichText content={body} />}</div>
          )}
          {callToAction && (
            <div className="mt-4 flex justify-center">
              <CallToAction content={callToAction} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IconWithText;
